import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TBuilderToolbarMenuViewPresentation } from "components/builderShell/BuilderToolbarMenuView";
import { BuilderToolbarMenuView } from "components/builderShell/BuilderToolbarMenuView";
import { BuilderToolbarMenuSubViewList } from "components/builderShell/BuilderToolbarMenuSubViewList";
import { BottomSheet } from "components/bottomSheetDialog";
import "components/builderShell/builderToolbarMenuView.scss";
import { EmailBuilderDefaultColorsToolbarMenu } from "./EmailBuilderDefaultColorsToolbarMenu";
import { EmailBuilderGeneralToolbarMenu } from "./EmailBuilderGeneralToolbarMenu";
import { resolveEmailBuilderCustomizeSubSheetSnapPoints } from "../emailBuilderPanelSheetSnapPoints";
import {
  EMAIL_BUILDER_SETTINGS_SHEET_OVERLAY_PORTAL_ID,
  EmailBuilderSettingsOverlayPortalProvider,
} from "../../canvas/settings/emailBuilderSettingsOverlayPortal";

type TEmailBuilderCustomizeSubSheet = "colors" | "general";

type TEmailBuilderCustomizeSubView = "none" | TEmailBuilderCustomizeSubSheet;

const SUB_SHEET_EXIT_MS = 600;

interface IEmailBuilderCustomizeToolbarMenuProps {
  presentation?: TBuilderToolbarMenuViewPresentation;
  onDismissPanel?: () => void;
  onClose?: () => void;
  closeAriaLabel?: string;
}

export function EmailBuilderCustomizeToolbarMenu(props: IEmailBuilderCustomizeToolbarMenuProps) {
  const { t } = useTranslation();
  const [subView, setSubView] = useState<TEmailBuilderCustomizeSubView>("none");
  const [subSheetView, setSubSheetView] = useState<TEmailBuilderCustomizeSubSheet | null>(null);
  const [subSheetOpen, setSubSheetOpen] = useState(false);
  const subSheetUnmountTimerRef = useRef<number | null>(null);
  const presentation = props.presentation ?? "desktop-panel";
  const isMobileSheet = presentation === "mobile-sheet-body";

  const hubOptions = React.useMemo(
    () => [
      { id: "general" as const, title: t("General") },
      { id: "colors" as const, title: t("Default Colors") },
    ],
    [t],
  );

  useEffect(() => {
    return () => {
      if (subSheetUnmountTimerRef.current !== null) {
        window.clearTimeout(subSheetUnmountTimerRef.current);
      }
    };
  }, []);

  function clearSubSheetUnmountTimer() {
    if (subSheetUnmountTimerRef.current !== null) {
      window.clearTimeout(subSheetUnmountTimerRef.current);
      subSheetUnmountTimerRef.current = null;
    }
  }

  function openMobileSubSheet(id: TEmailBuilderCustomizeSubSheet) {
    clearSubSheetUnmountTimer();
    setSubView(id);
    setSubSheetView(id);
    setSubSheetOpen(true);
  }

  function handleRequestGoBackToHub() {
    setSubView("none");
  }

  function handleMobileSubSheetOpenChange(open: boolean) {
    setSubSheetOpen(open);
    if (!open) {
      handleRequestGoBackToHub();
      clearSubSheetUnmountTimer();
      subSheetUnmountTimerRef.current = window.setTimeout(() => {
        setSubSheetView(null);
        subSheetUnmountTimerRef.current = null;
      }, SUB_SHEET_EXIT_MS);
    }
  }

  function renderSubViewContent(subViewId: TEmailBuilderCustomizeSubSheet) {
    if (subViewId === "colors") {
      return (
        <EmailBuilderDefaultColorsToolbarMenu
          presentation={presentation}
          panelTitle={t("Default Colors")}
          onClose={isMobileSheet ? undefined : props.onClose}
          closeAriaLabel={isMobileSheet ? undefined : props.closeAriaLabel}
          goBackToParentView={isMobileSheet ? undefined : handleRequestGoBackToHub}
        />
      );
    }

    return (
      <EmailBuilderGeneralToolbarMenu
        presentation={presentation}
        panelTitle={t("General")}
        goBackToParentView={isMobileSheet ? undefined : handleRequestGoBackToHub}
        onClose={isMobileSheet ? undefined : props.onClose}
        closeAriaLabel={isMobileSheet ? undefined : props.closeAriaLabel}
      />
    );
  }

  if (!isMobileSheet) {
    if (subView === "colors") {
      return renderSubViewContent("colors");
    }

    if (subView === "general") {
      return renderSubViewContent("general");
    }
  }

  const mobileSubViewTitle =
    subSheetView === "colors"
      ? t("Default Colors")
      : subSheetView === "general"
        ? t("General")
        : "";
  const mobileSubSheetSnapPoints =
    subSheetView !== null ? resolveEmailBuilderCustomizeSubSheetSnapPoints(subSheetView) : null;

  return (
    <>
      <BuilderToolbarMenuView
        title={t("Customize")}
        presentation={presentation}
        noSectionsPadding
        onClose={props.onClose}
        closeAriaLabel={props.closeAriaLabel}
        sections={[
          {
            content: (
              <BuilderToolbarMenuSubViewList
                options={hubOptions}
                onSelect={id => {
                  if (id === "colors" || id === "general") {
                    if (isMobileSheet) {
                      openMobileSubSheet(id);
                      return;
                    }

                    setSubView(id);
                  }
                }}
              />
            ),
          },
        ]}
      />

      {isMobileSheet && subSheetView !== null && mobileSubSheetSnapPoints !== null ? (
        <BottomSheet.Root
          key={subSheetView}
          open={subSheetOpen}
          onOpenChange={handleMobileSubSheetOpenChange}
          dismissible
          modal
          snapPoints={mobileSubSheetSnapPoints.snapPoints}
          defaultSnapPoint={mobileSubSheetSnapPoints.defaultSnapPoint}
        >
          <BottomSheet.Portal>
            <BottomSheet.Backdrop />
            <BottomSheet.Popup className="email-builder-panel-bottom-sheet email-builder-customize-sub-sheet">
              <BottomSheet.Header>
                <BottomSheet.Title>{mobileSubViewTitle}</BottomSheet.Title>
                <BottomSheet.Close aria-label={t("Close panel")} />
              </BottomSheet.Header>
              <BottomSheet.Body className="email-builder-panel-sheet-body">
                <EmailBuilderSettingsOverlayPortalProvider
                  portalId={EMAIL_BUILDER_SETTINGS_SHEET_OVERLAY_PORTAL_ID}
                >
                  {renderSubViewContent(subSheetView)}
                </EmailBuilderSettingsOverlayPortalProvider>
              </BottomSheet.Body>
            </BottomSheet.Popup>
            <div
              id={EMAIL_BUILDER_SETTINGS_SHEET_OVERLAY_PORTAL_ID}
              className="email-builder-customize-sub-sheet-overlays"
            />
          </BottomSheet.Portal>
        </BottomSheet.Root>
      ) : null}
    </>
  );
}
